import {
  CreateSponsorRequestPayload,
  UpdateSponsorRequestPayload,
} from 'app/api/sponsorRequest'
import { formatCurrencyToNumber } from 'app/utils/currencyUtils'
import {
  formatApiStringToDate,
  formatDateToApiString,
} from 'app/utils/dateUtils'
import {
  SponsorRequest,
  SponsorRequestEvent,
  SponsorRequestScope,
  SponsorRequestTimeline,
} from 'EstraSponsor'
import * as Yup from 'yup'

export type SponsorRequestEventsFormData = {
  start_on: Maybe<Date>
  end_on: Maybe<Date>
  attendees: Maybe<string>
}

export type SponsorRequestFormData = {
  name: string
  scope: Maybe<SponsorRequestScope>
  timeline: Maybe<SponsorRequestTimeline>
  brand_name: 'Prometeo' | 'Estra' | null | undefined
  previous_edition_attendees: Maybe<string>
  nr_events_for_season: Maybe<string>
  attendees_average_for_event: Maybe<string>
  purpose: Maybe<string>
  region_id: Maybe<string>
  province_id: Maybe<string>
  city_id: Maybe<string>
  place: Maybe<string>
  target_audience: Maybe<string>
  website_link: Maybe<string>
  facebook_link: Maybe<string>
  instagram_link: Maybe<string>
  youtube_link: Maybe<string>
  twitter_link: Maybe<string>
  tiktok_link: Maybe<string>
  financial_contribution: Maybe<string>
  technical_contribution: Maybe<string>
  logo_visibility: Maybe<string>
  benefits: Maybe<string>
  press_office_name: Maybe<string>
  press_office_email: Maybe<string>
  press_office_phone_number: Maybe<string>
  commercial_referent_name: Maybe<string>
  commercial_referent_email: Maybe<string>
  commercial_referent_phone_number: Maybe<string>
  ethic_code_accepted: Maybe<boolean>
  anti_corruption_policy_accepted: Maybe<boolean>
  sponsor_request_events: SponsorRequestEventsFormData[]
}

export const sponsorRequestEventDefaultValues: SponsorRequestEventsFormData = {
  start_on: null,
  end_on: null,
  attendees: '0',
}

export const sponsorRequestDefaultValues: SponsorRequestFormData = {
  name: '',
  scope: null,
  timeline: null,
  brand_name: null,
  previous_edition_attendees: null,
  nr_events_for_season: null,
  attendees_average_for_event: null,
  purpose: null,
  region_id: '',
  province_id: null,
  city_id: null,
  place: null,
  target_audience: null,
  website_link: null,
  facebook_link: null,
  instagram_link: null,
  youtube_link: null,
  twitter_link: null,
  tiktok_link: null,
  financial_contribution: null,
  technical_contribution: null,
  logo_visibility: null,
  benefits: null,
  press_office_name: null,
  press_office_email: null,
  press_office_phone_number: null,
  commercial_referent_name: null,
  commercial_referent_email: null,
  commercial_referent_phone_number: null,
  ethic_code_accepted: null,
  anti_corruption_policy_accepted: null,
  sponsor_request_events: [],
}

// Minimum date is 30 days from now
const nextMonthDate = new Date()
nextMonthDate.setDate(nextMonthDate.getDate() + 30)
nextMonthDate.setHours(0, 0, 0, 0)

const reg =
  /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/

export const sponsorRequestValidationSchema = Yup.object().shape(
  {
    name: Yup.string().required('Campo obbligatorio'),
    scope: Yup.string().nullable(true).required('Campo obbligatorio'),
    timeline: Yup.string().nullable(true).required('Campo obbligatorio'),
    previous_edition_attendees: Yup.string()
      .nullable(true)
      // Transform empty string to null, to avoid validation error when empty string
      .transform((value, originalValue) =>
        typeof originalValue === 'string' && originalValue === '' ? null : value
      ),
    nr_events_for_season: Yup.string()
      .nullable(true)
      // Transform empty string to null, to avoid validation error when empty string
      .transform((value, originalValue) =>
        typeof originalValue === 'string' && originalValue === '' ? null : value
      )
      .when('timeline', {
        is: (timeline: SponsorRequestFormData['timeline']) =>
          timeline && timeline === 'season',
        then: (rule) => rule.required('Campo obbligatorio'),
      }),
    attendees_average_for_event: Yup.string()
      .nullable(true)
      // Transform empty string to null, to avoid validation error when empty string
      .transform((value, originalValue) =>
        typeof originalValue === 'string' && originalValue === '' ? null : value
      )
      .when('timeline', {
        is: (timeline: SponsorRequestFormData['timeline']) =>
          timeline && timeline === 'season',
        then: (rule) => rule.required('Campo obbligatorio'),
      }),
    purpose: Yup.string().nullable(true).required('Campo obbligatorio'),
    region_id: Yup.string().required('Campo obbligatorio'),
    place: Yup.string().nullable(true).required('Campo obbligatorio'),
    target_audience: Yup.string().nullable(true).required('Campo obbligatorio'),
    // At least one of technical_contribution and financial_contribution is required
    // but not both
    technical_contribution: Yup.string()
      .nullable(true)
      .test(
        'onlyOneContribution',
        'Inserire solo un tipo di contributo, economico o tecnico',
        function (technical_contribution) {
          const { financial_contribution } = this.parent
          if (
            technical_contribution &&
            technical_contribution.length !== 0 &&
            financial_contribution &&
            financial_contribution.length !== 0
          ) {
            return false
          }
          return true
        }
      )
      .when('financial_contribution', {
        is: (
          financial_contribution: SponsorRequestFormData['financial_contribution']
        ) => !financial_contribution || financial_contribution.length === 0,
        then: (rule) =>
          rule.required(
            'Inserire solo un tipo di contributo, economico o tecnico'
          ),
      }),
    financial_contribution: Yup.number()
      .nullable(true)
      .min(0, 'Inserire un numero maggiore o uguale a 0')
      // Transform empty string to null, to avoid validation error when empty string
      .transform((_value, originalValue) =>
        typeof originalValue === 'string' && originalValue === ''
          ? null
          : Number(formatCurrencyToNumber(originalValue))
      )
      .integer('Inserire un numero, non decimale')
      .test(
        'onlyOneContribution',
        'Inserire solo un tipo di contributo, economico o tecnico',
        function (financial_contribution) {
          const { technical_contribution } = this.parent
          if (
            technical_contribution &&
            technical_contribution.length !== 0 &&
            financial_contribution &&
            financial_contribution.toString().length !== 0
          ) {
            return false
          }
          return true
        }
      )
      .when('technical_contribution', {
        is: (
          technical_contribution: SponsorRequestFormData['technical_contribution']
        ) => !technical_contribution || technical_contribution.length === 0,
        then: (rule) =>
          rule.required(
            'Inserire solo un tipo di contributo, economico o tecnico'
          ),
      }),
    logo_visibility: Yup.string().nullable(true).required('Campo obbligatorio'),
    press_office_phone_number: Yup.string()
      .nullable(true)
      .matches(/^[0-9]*$/, `Numero non valido`) // Transform empty string to null, to avoid validation error when empty string
      .transform((value, originalValue) =>
        typeof originalValue === 'string' && originalValue === '' ? null : value
      ),
    press_office_email: Yup.string()
      .nullable(true)
      .email('Indirizzo email non valido') // Transform empty string to null, to avoid validation error when empty string
      .transform((value, originalValue) =>
        typeof originalValue === 'string' && originalValue === '' ? null : value
      ),
    commercial_referent_name: Yup.string()
      .nullable(true)
      .required('Campo obbligatorio'),
    commercial_referent_email: Yup.string()
      .nullable(true)
      .required('Campo obbligatorio')
      .email('Indirizzo email non valido'),
    commercial_referent_phone_number: Yup.string()
      .nullable(true)
      .required('Campo obbligatorio')
      .matches(/^[0-9]*$/, 'Numero non valido'),
    website_link: Yup.string()
      .matches(reg, 'Inserire un indirizzo valido')
      .nullable(true)
      // Transform empty string to null, to avoid validation error when empty string
      .transform((value, originalValue) =>
        typeof originalValue === 'string' && originalValue === '' ? null : value
      ),
    facebook_link: Yup.string()
      .matches(reg, 'Inserire un indirizzo valido')
      .nullable(true)
      // Transform empty string to null, to avoid validation error when empty string
      .transform((value, originalValue) =>
        typeof originalValue === 'string' && originalValue === '' ? null : value
      ),
    tiktok_link: Yup.string()
      .matches(reg, 'Inserire un indirizzo valido')
      .nullable(true)
      // Transform empty string to null, to avoid validation error when empty string
      .transform((value, originalValue) =>
        typeof originalValue === 'string' && originalValue === '' ? null : value
      ),
    instagram_link: Yup.string()
      .matches(reg, 'Inserire un indirizzo valido')
      .nullable(true)
      // Transform empty string to null, to avoid validation error when empty string
      .transform((value, originalValue) =>
        typeof originalValue === 'string' && originalValue === '' ? null : value
      ),
    twitter_link: Yup.string()
      .matches(reg, 'Inserire un indirizzo valido')
      .nullable(true)
      // Transform empty string to null, to avoid validation error when empty string
      .transform((value, originalValue) =>
        typeof originalValue === 'string' && originalValue === '' ? null : value
      ),
    youtube_link: Yup.string()
      .matches(reg, 'Inserire un indirizzo valido')
      .nullable(true)
      // Transform empty string to null, to avoid validation error when empty string
      .transform((value, originalValue) =>
        typeof originalValue === 'string' && originalValue === '' ? null : value
      ),

    sponsor_request_events: Yup.array().of(
      Yup.object().shape({
        start_on: Yup.date()
          .nullable(true)
          .required('Campo obbligatorio')
          .min(
            nextMonthDate,
            'La data di inizio deve essere di almeno 30 giorni successiva alla data odierna'
          ),
        end_on: Yup.date()
          .nullable(true)
          .required('Campo obbligatorio')
          .min(
            Yup.ref('start_on'),
            'La data di fine non può essere antecedente alla data di inizio'
          ),

        attendees: Yup.string()
          .nullable(true)
          .required('Campo obbligatorio')
          // Transform empty string to null, to avoid validation error when empty string
          .transform((value, originalValue) =>
            typeof originalValue === 'string' && originalValue === ''
              ? null
              : value
          ),
      })
    ),
    ethic_code_accepted: Yup.bool()
      .nullable(true)
      .oneOf([true], 'Consenso obbligatorio'),
    anti_corruption_policy_accepted: Yup.bool()
      .nullable(true)
      .oneOf([true], 'Consenso obbligatorio'),
  },
  [['financial_contribution', 'technical_contribution']] // Fix cyclic dependency error
)

export const adaptApiToFormData = (
  apiData: SponsorRequest
): SponsorRequestFormData => {
  const formData: SponsorRequestFormData = {
    name: apiData.name,
    scope: apiData.scope,
    timeline: apiData.timeline,
    brand_name: apiData.brand_name,
    previous_edition_attendees: apiData.previous_edition_attendees,
    nr_events_for_season: apiData.nr_events_for_season,
    attendees_average_for_event: apiData.attendees_average_for_event,
    purpose: apiData.purpose,
    region_id: apiData.region?.id.toString(),
    province_id: apiData.province?.id.toString(),
    city_id: apiData.city?.id.toString(),
    place: apiData.place,
    target_audience: apiData.target_audience,
    website_link: apiData.website_link,
    tiktok_link: apiData.tiktok_link,
    facebook_link: apiData.facebook_link,
    youtube_link: apiData.youtube_link,
    instagram_link: apiData.instagram_link,
    twitter_link: apiData.twitter_link,
    financial_contribution: apiData.financial_contribution
      ? parseInt(apiData.financial_contribution).toString()
      : '',
    technical_contribution: apiData.technical_contribution,
    logo_visibility: apiData.logo_visibility,
    benefits: apiData.benefits,
    press_office_name: apiData.press_office_name,
    press_office_email: apiData.press_office_email,
    press_office_phone_number: apiData.press_office_phone_number,
    commercial_referent_name: apiData.commercial_referent_name,
    commercial_referent_email: apiData.commercial_referent_email,
    commercial_referent_phone_number: apiData.commercial_referent_phone_number,
    ethic_code_accepted: apiData.ethic_code_accepted,
    anti_corruption_policy_accepted: apiData.anti_corruption_policy_accepted,
    sponsor_request_events: [],
  }

  formData.sponsor_request_events = adaptEventsApiToFormData(
    apiData.sponsor_request_events || [],
    formData.timeline
  )

  return formData
}

const adaptEventsApiToFormData = (
  eventsApiData: SponsorRequestEvent[],
  timeline: SponsorRequestFormData['timeline']
): SponsorRequestEventsFormData[] => {
  if (!timeline) return []

  const eventsFormData = eventsApiData.map((event) => ({
    start_on: formatApiStringToDate(event.start_on) as Date,
    end_on: formatApiStringToDate(event.end_on) as Date,
    attendees: event.attendees,
  }))

  // Add items with default values based in the timeline value.
  // Ex. If timeline value is multiple it needs at least two event items
  if (timeline === 'single') {
    return [eventsFormData[0] || sponsorRequestEventDefaultValues]
  } else if (timeline === 'season') {
    return eventsFormData.length === 0
      ? [sponsorRequestEventDefaultValues]
      : eventsFormData
  } else {
    // multiple
    return eventsFormData.length === 0
      ? [sponsorRequestEventDefaultValues]
      : eventsFormData.length === 1
      ? [...eventsFormData, sponsorRequestEventDefaultValues]
      : eventsFormData
  }
}

export const adaptFormDataToApi = (
  formData: SponsorRequestFormData
): CreateSponsorRequestPayload | UpdateSponsorRequestPayload => ({
  name: formData.name,
  scope: formData.scope,
  brand_name: formData.brand_name,
  timeline: formData.timeline,
  previous_edition_attendees: formData.previous_edition_attendees,
  nr_events_for_season: formData.nr_events_for_season,
  attendees_average_for_event: formData.attendees_average_for_event,
  purpose: formData.purpose,
  region_id: formData.region_id,
  province_id: formData.province_id,
  city_id: formData.city_id,
  place: formData.place,
  target_audience: formData.target_audience,
  website_link: formData.website_link,
  tiktok_link: formData.tiktok_link,
  facebook_link: formData.facebook_link,
  youtube_link: formData.youtube_link,
  instagram_link: formData.instagram_link,
  twitter_link: formData.twitter_link,
  financial_contribution: formatCurrencyToNumber(
    formData.financial_contribution
  ),
  technical_contribution: formData.technical_contribution,
  logo_visibility: formData.logo_visibility,
  benefits: formData.benefits,
  press_office_name: formData.press_office_name,
  press_office_email: formData.press_office_email,
  press_office_phone_number: formData.press_office_phone_number,
  commercial_referent_name: formData.commercial_referent_name,
  commercial_referent_email: formData.commercial_referent_email,
  commercial_referent_phone_number: formData.commercial_referent_phone_number,
  sponsor_request_events_attributes: formData.sponsor_request_events
    ? formData.sponsor_request_events.map((event) => ({
        start_on: formatDateToApiString(event.start_on) as string,
        end_on: formatDateToApiString(event.end_on) as string,
        attendees: event.attendees,
      }))
    : [],
  ethic_code_accepted: formData.ethic_code_accepted,
  anti_corruption_policy_accepted: formData.anti_corruption_policy_accepted,
})
